import { useEffect, useState } from 'react';

export type TimeOfDay = 'morning' | 'day' | 'evening' | 'night';

const TIME_QUERY_PARAM = 'time';

const getTimeOfDayFromHour = (hour: number): TimeOfDay => {
  if (hour >= 6 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 18) return 'day';
  if (hour >= 18 && hour < 23) return 'evening';
  return 'night';
};

const isValidTimeOfDay = (value: string | null): value is TimeOfDay =>
  value === 'morning' || value === 'day' || value === 'evening' || value === 'night';

const readOverride = (): TimeOfDay | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const override = params.get(TIME_QUERY_PARAM);
  return isValidTimeOfDay(override) ? override : null;
};

const computeTimeOfDay = (now: Date = new Date()): TimeOfDay =>
  readOverride() ?? getTimeOfDayFromHour(now.getHours());

export const useTimeOfDay = (refreshMs = 60_000): TimeOfDay => {
  const [state, setState] = useState<TimeOfDay>(() => computeTimeOfDay());

  useEffect(() => {
    if (readOverride() !== null) return undefined;

    const tick = () => setState(computeTimeOfDay());
    const interval = window.setInterval(tick, refreshMs);
    return () => window.clearInterval(interval);
  }, [refreshMs]);

  return state;
};

export const __test = { getTimeOfDayFromHour, computeTimeOfDay };
